import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, MailX } from "lucide-react";

// Module-level promise cache so React StrictMode's dev double-invocation
// (which recreates refs on synthetic re-mount) never fires the one-time
// verify request twice. Both mounts await the same promise.
const inflight = new Map();

function verifyOnce(token) {
  if (!inflight.has(token)) {
    inflight.set(token, api.post("/auth/magic/verify", { token }).then((r) => r.data));
  }
  return inflight.get(token);
}

export default function MagicVerify() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const token = new URLSearchParams(window.location.search).get("token");
    (async () => {
      if (!token) {
        setError("This sign-in link is missing its token.");
        return;
      }
      try {
        const data = await verifyOnce(token);
        if (cancelled) return;
        setUser(data.user);
        window.history.replaceState({}, "", "/app");
        toast.success(data.is_new_user ? "Welcome to b→come." : "Signed in.");
        navigate(data.user.onboarded ? "/app" : "/onboarding", { replace: true });
      } catch (e) {
        if (cancelled) return;
        const detail = e.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "This sign-in link is invalid or has expired.");
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, setUser]);

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center px-5">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0C] p-10 text-center"
        data-testid="magic-verify-page">
        {error ? (
          <>
            <MailX className="mx-auto h-10 w-10 text-[#FF7E67]" />
            <h1 className="mt-4 font-mega text-2xl text-white">Link no longer valid</h1>
            <p className="mt-3 text-sm text-white/60" data-testid="magic-verify-error">{error}</p>
            <Link to="/login" data-testid="magic-back-to-login"
              className="mt-6 inline-flex h-11 items-center justify-center rounded-full bg-gradient-brand px-6 font-bold uppercase tracking-wide text-black">
              Request a new link
            </Link>
          </>
        ) : (
          <div className="flex flex-col items-center gap-4 text-white/70">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
            Signing you in…
          </div>
        )}
      </div>
    </div>
  );
}
